import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth, AUTH_STATES } from './contexts/AuthContext';
import { ClientAuthProvider } from './contexts/ClientAuthContext';
import Dashboard from './components/Dashboard';
import CandidateDetail from './components/CandidateDetail';
import NPSSegmentDetail from './components/NPSSegmentDetail';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import UserManagement from './components/admin/UserManagement';
import ClientAccessPanel from './components/admin/ClientAccessPanel';
import PortalLanding from './components/client/PortalLanding';
import ClientDashboard from './components/client/ClientDashboard';
import ClientLogout from './components/client/ClientLogout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary, { AuthErrorFallback } from './components/ErrorBoundary';
import { PERMISSIONS } from './constants/roles';
import './index.css';

const AuthenticatedApp = () => {
  const { isLoading, login, state, error, hasError, isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Check if we're on a public route that doesn't require authentication
  const isClientRoute = location.pathname.startsWith('/portal') || location.pathname === '/client-dashboard' || location.pathname === '/client-logout';
  const isPublicRoute = ['/login', '/auth/callback'].includes(location.pathname) || location.pathname.startsWith('/portal') || location.pathname === '/client-logout';

  // Skip internal auth loading for client portal routes
  if (isLoading && !isClientRoute && (!isPublicRoute || isAuthenticated)) {
    const getLoadingContent = () => {
      switch (state) {
        case AUTH_STATES.INITIALIZING:
          return {
            title: 'Initializing Application',
            message: 'Checking your authentication status...'
          };
        case AUTH_STATES.AUTHENTICATING:
          return {
            title: 'Authenticating',
            message: 'Verifying your credentials...'
          };
        case AUTH_STATES.OAUTH_CALLBACK:
          return {
            title: 'Processing Sign In',
            message: 'Completing your authentication...'
          };
        default:
          return {
            title: 'Loading',
            message: 'Please wait while we prepare your dashboard...'
          };
      }
    };

    const loadingContent = getLoadingContent();

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="dashboard-card p-8 text-center fade-in max-w-md">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-color mx-auto mb-6"></div>
          <h2 className="text-xl font-medium text-gray-700 mb-2">{loadingContent.title}</h2>
          <p className="text-gray-500">{loadingContent.message}</p>
          
          {/* Progress indicator */}
          <div className="w-full bg-gray-200 rounded-full h-1 mt-6">
            <div className="bg-primary-color h-1 rounded-full animate-pulse" style={{width: '45%'}}></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Show error state if there's a persistent error
  if (hasError && error && !error.retryable) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="dashboard-card p-8 text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Application</h2>
          <p className="text-gray-600 mb-4">{error.userMessage}</p>
          <button 
            onClick={() => window.location.href = '/login'}
            className="bg-primary-color text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/login" element={<Login onLogin={login} />} />
          
          {/* Client Portal Routes (public — auth handled by ClientAuthContext) */}
          <Route path="/portal/:token" element={<PortalLanding />} />
          <Route path="/client-dashboard" element={<ClientDashboard />} />
          <Route path="/client-logout" element={<ClientLogout />} />
          
          {/* Protected Routes */}
          <Route path="/admin/clients" element={
            <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_CLIENT_ACCESS} showFallback={true}>
              <ClientAccessPanel />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users" element={
            <ProtectedRoute requiredPermission={PERMISSIONS.MANAGE_USERS} showFallback={true}>
              <UserManagement />
            </ProtectedRoute>
          } />
          
          <Route path="/candidate/:candidateId" element={
            <ProtectedRoute>
              <CandidateDetail />
            </ProtectedRoute>
          } />
          
          <Route path="/nps/:segment" element={
            <ProtectedRoute requiredPermission={PERMISSIONS.VIEW_INDIVIDUAL_CLIENT_NPS}>
              <NPSSegmentDetail />
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/" element={
            <ProtectedRoute fallbackPath="/login">
              <Dashboard />
            </ProtectedRoute>
          } />
        </Routes>
    </div>
  );
};

const App = () => {
  return (
    <ErrorBoundary 
      fallback={AuthErrorFallback}
      showDetails={process.env.NODE_ENV === 'development'}
      maxRetries={3}
    >
      <Router>
        <AuthProvider>
          <ClientAuthProvider>
            <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
              <AuthenticatedApp />
            </ErrorBoundary>
          </ClientAuthProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
